import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';
import { listDriveFolder } from '../drive/api';
import { parseDriveFolderUrl } from '../../lib/parseDriveFolderUrl';
import type { PostMedia } from '../../types/publishing';
import { MEDIA_CONTENT_TYPES, validateMediaFile } from '../../lib/publishing/mediaUpload';
import { mediaSortIds, moveMedia } from '../../lib/publishing/mediaOrder';
import { addMediaFromDrive, uploadMedia } from './api';

interface UploadState {
  name: string;
  index: number;
  total: number;
  percent: number | null;
}

// The broad filter for the DRIVE browser's listing (it shows what's pickable in a
// folder). Native files get the precise per-file check instead — see addFiles.
const MEDIA_MIME = /^(image|video)\//;

// Publishing composer — base media picker. Three sources: drag-and-drop, native
// file browse, and a Drive folder browse. Drops route through the SAME upload
// handler as browse, so the accepted types and their messaging apply identically.
// Phase 1: image + video, a thumbnail strip with remove.
//
// REORDER (2026-09-30): the thumbnails drag-reorder via dnd-kit (the survey
// StepList precedent) and the carousel publishes in array order. The drag
// starts from a small GRIP on each thumb, not the whole thumb, so the remove
// button stays an ordinary click. Keyboard reorder comes free from dnd-kit's
// KeyboardSensor (focus the grip, Space, arrows, Space). ⚠️ This component
// reorders ONLY the array it was handed — the base media and each destination
// override are separate MediaPicker instances with their own onChange. dnd-kit
// uses pointer events, not HTML5 drag, so it does not trip the file drop zone.
//
// ⚠️ A native file is validated HERE, per file, against the shared allowlist
// (src/lib/publishing/mediaUpload.ts) before anything hits the network — so an
// iPhone .heic or an oversized file fails immediately, naming itself, instead of
// late and cryptically. Then it is PUT straight to Zernio's storage from the
// browser; our server only presigns. Progress is shown because a 200 MB video
// with no feedback reads as a hung app.

export function MediaPicker({
  media,
  onChange,
  disabled,
}: {
  media: PostMedia[];
  onChange: (next: PostMedia[]) => void;
  disabled?: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [driveOpen, setDriveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<UploadState | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // dragenter/leave fire on every child boundary; a depth counter keeps the
  // highlight steady instead of flickering as the cursor crosses thumbs/buttons.
  const dragDepth = useRef(0);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const sortIds = mediaSortIds(media);
  const canReorder = !disabled && media.length > 1;
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    onChange(moveMedia(media, String(e.active.id), String(e.over.id)));
  };

  // The ONE upload path, shared by browse + drop. Each file is checked against
  // the shared allowlist FIRST (so a rejection names the file and the reason,
  // before any network call), then PUT direct to storage with progress.
  async function addFiles(files: File[]) {
    if (files.length === 0) return;
    const accepted: File[] = [];
    for (const file of files) {
      const check = validateMediaFile({ mimeType: file.type, sizeBytes: file.size });
      if (check.ok) accepted.push(file);
      // Name the file — with a multi-file drop, "unsupported type" alone leaves
      // the AM guessing which one was dropped.
      else toast.error(`${file.name || 'That file'}: ${check.message}`);
    }
    if (accepted.length === 0) return;

    setBusy(true);
    try {
      const added: PostMedia[] = [];
      for (const [index, file] of accepted.entries()) {
        setUpload({ name: file.name, index: index + 1, total: accepted.length, percent: null });
        added.push(
          await uploadMedia(file, {
            onProgress: (p) =>
              setUpload({
                name: file.name,
                index: index + 1,
                total: accepted.length,
                percent: p.percent,
              }),
          }),
        );
      }
      onChange([...media, ...added]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      setUpload(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const dropHandlers = disabled
    ? {}
    : {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          dragDepth.current += 1;
          setDragOver(true);
        },
        // preventDefault on dragover is required for the drop to fire at all.
        onDragOver: (e: React.DragEvent) => e.preventDefault(),
        onDragLeave: () => {
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) {
            dragDepth.current = 0;
            setDragOver(false);
          }
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          const files = Array.from(e.dataTransfer?.files ?? []);
          if (files.length > 0) void addFiles(files);
        },
      };

  return (
    <div className="space-y-2">
      <div
        data-testid="media-drop-zone"
        {...dropHandlers}
        className={cn(
          'rounded-lg border border-dashed p-3 transition-colors',
          dragOver ? 'border-kyck-500 bg-kyck-50' : 'border-slate-200',
        )}
      >
        <div className="flex flex-wrap gap-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={sortIds} strategy={rectSortingStrategy}>
              {media.map((m, idx) => (
                <MediaThumb
                  key={sortIds[idx]}
                  sortId={sortIds[idx]!}
                  position={idx + 1}
                  media={m}
                  disabled={disabled}
                  reorderable={canReorder}
                  onRemove={() => onChange(media.filter((_, i) => i !== idx))}
                />
              ))}
            </SortableContext>
          </DndContext>
          {!disabled && (
            <div className="flex flex-col gap-2">
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
                {busy ? 'Uploading…' : 'Browse'}
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setDriveOpen(true)}>
                From Drive
              </Button>
            </div>
          )}
        </div>
        {upload && <UploadProgressBar upload={upload} />}
        {!disabled && !upload && (
          <p className="mt-2 text-xs text-slate-400">
            Drag files here, or browse / paste a Drive link.
          </p>
        )}
      </div>
      {/* accept is the exact allowlist, not image/*,video/* — the OS dialog then
          greys out a .heic instead of letting it be picked and rejected a moment
          later. */}
      <input
        ref={fileInput}
        data-testid="media-file-input"
        type="file"
        accept={MEDIA_CONTENT_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
      />

      <DrivePickerDialog
        open={driveOpen}
        onClose={() => setDriveOpen(false)}
        onPick={async (fileId) => {
          setBusy(true);
          try {
            const added = await addMediaFromDrive(fileId);
            onChange([...media, added]);
            setDriveOpen(false);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not add from Drive');
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

// A large video takes real time to upload, and the only thing worse than a slow
// upload is one that gives no sign of moving. Percent where the browser reports a
// total, an indeterminate bar where it doesn't (never a fake number).
function UploadProgressBar({ upload }: { upload: UploadState }) {
  const label =
    upload.total > 1
      ? `Uploading ${upload.index} of ${upload.total} — ${upload.name}`
      : `Uploading ${upload.name}`;
  return (
    <div className="mt-2" data-testid="media-upload-progress">
      <div className="flex items-baseline justify-between gap-2 text-xs text-slate-500">
        <span className="truncate">{label}</span>
        {upload.percent !== null && (
          <span className="tabular-nums text-slate-600">{upload.percent}%</span>
        )}
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={cn(
            'h-full rounded-full bg-kyck-500 transition-[width]',
            upload.percent === null && 'w-1/3 animate-pulse',
          )}
          style={upload.percent !== null ? { width: `${upload.percent}%` } : undefined}
        />
      </div>
    </div>
  );
}

function MediaThumb({
  sortId,
  position,
  media,
  onRemove,
  disabled,
  reorderable,
}: {
  sortId: string;
  position: number;
  media: PostMedia;
  onRemove: () => void;
  disabled?: boolean;
  reorderable: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
    disabled: !reorderable,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative h-20 w-20 rounded-md overflow-hidden border border-slate-200 bg-slate-900"
    >
      {media.type === 'video' ? (
        <video src={media.url} className="h-full w-full object-cover" muted />
      ) : (
        <img src={media.thumbnailUrl ?? media.url} alt="" className="h-full w-full object-cover" />
      )}
      <span className="absolute bottom-0 left-0 bg-black/60 px-1 text-[9px] text-white">
        {media.type}
      </span>
      {reorderable && (
        <button
          type="button"
          className="absolute top-0.5 left-0.5 h-5 w-5 cursor-grab touch-none rounded bg-black/60 text-white active:cursor-grabbing"
          aria-label={`Drag to reorder (item ${position})`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="mx-auto h-3.5 w-3.5" />
        </button>
      )}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white text-xs leading-none"
          aria-label="Remove"
        >
          ×
        </button>
      )}
    </div>
  );
}

function DrivePickerDialog({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (fileId: string) => void;
}) {
  const [url, setUrl] = useState('');
  const folderId = parseDriveFolderUrl(url.trim());
  const listing = useQuery({
    queryKey: ['publishing-drive-folder', folderId],
    queryFn: () => listDriveFolder(folderId!),
    enabled: open && !!folderId,
    staleTime: 30 * 60 * 1000,
  });
  const files = (listing.data?.files ?? []).filter((f) => MEDIA_MIME.test(f.mimeType));

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add media from Drive</DialogTitle>
        </DialogHeader>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste a Google Drive folder URL"
        />
        {url.trim() !== '' && !folderId && (
          <p className="text-sm text-amber-700">Couldn't read a folder id from that URL.</p>
        )}
        {listing.isLoading && <CenteredSpinner />}
        {listing.isError && <p className="text-sm text-red-600">Couldn't load that Drive folder.</p>}
        {folderId && !listing.isLoading && files.length === 0 && (
          <p className="text-sm text-slate-500">No images or videos in this folder.</p>
        )}
        <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto">
          {files.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onPick(f.id)}
              className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-900 hover:ring-2 hover:ring-slate-400"
            >
              {f.thumbnailUrl ? (
                <img src={f.thumbnailUrl} alt={f.name} className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full items-center justify-center text-[10px] text-slate-400">
                  {f.name}
                </span>
              )}
              <span className="absolute bottom-0 inset-x-0 truncate bg-black/60 px-1 text-[9px] text-white">
                {f.name}
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
